import { useEffect } from 'react';
import { Badge, Box, Button, Card, Flex, Icon, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import { ArrowLeftIcon, BookOpenIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import { useAuth } from '@/lib/hooks/use-auth';
import useCurriculumsStore from '@/utils/use-curriculums';
import { Curriculum, PlanScope } from '@/lib/types';
import { PlannerAction } from '@/pages/planner/planner-machine';
import CurOutcomesSummary from './cur-outcomes-summary';
import DatePill from './date-pill';

interface CurBrowseScreenProps {
  intent: 'manage' | 'addToDate';
  date?: string;
  // addToDate only: the day slot being filled (workspace plan or "My Plan"),
  // threaded through to curPlans and its eventual draft.
  scope?: PlanScope;
  dispatch: (action: PlannerAction) => void;
}

// "curBrowse" — header row (date pill + title + actions) over a 2-col grid of
// curriculum cards. With intent 'addToDate' (how every role arrives from the
// choice screen) clicking a card routes into the plan-picker flow (curPlans)
// for the selected date; with intent 'manage' (internal returns from the
// create wizard and curBuild's Done) it routes into the build flow (curBuild).
// Admins additionally see a "Create curriculum" button in either intent. Back
// returns to the choice screen for the date the user came from, or the empty
// panel when there is none (e.g. re-entry from the create-curriculum wizard,
// which drops the date).
export default function CurBrowseScreen({ intent, date, scope, dispatch }: CurBrowseScreenProps) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const isAdmin = useIsInAdminGroup();
  const { workspace } = useAuth();
  const items = useCurriculumsStore((s) => s.items);
  const isLoading = useCurriculumsStore((s) => s.isLoading);
  const load = useCurriculumsStore((s) => s.load);

  useEffect(() => {
    if (!workspaceId) return;
    load(workspaceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const handleOpen = (curriculum: Curriculum) => {
    dispatch({ type: 'OPEN_CURRICULUM', curriculumId: curriculum.id, date, intent, scope });
  };

  const handleBack = () => {
    dispatch({
      type: 'GO_TO',
      screen: date ? { name: 'choice', date, scope: scope ?? 'workspace' } : { name: 'empty' },
    });
  };

  return (
    <Stack gap={4}>
      <Flex align='center' gap={3} flexWrap='wrap' pb={3} borderBottomWidth='1px' borderColor='border'>
        {date && <DatePill date={date} />}
        <Text fontSize='lg' fontWeight='700' color='fg.strong'>
          {intent === 'manage' ? 'Curriculum library' : 'Choose a curriculum'}
        </Text>
        <Flex align='center' gap={2} ml='auto'>
          {isAdmin && (
            <Button colorPalette='brand' onClick={() => dispatch({ type: 'CREATE_CURRICULUM' })}>
              <Icon as={PlusIcon} w={4} h={4} />
              Create curriculum
            </Button>
          )}
          <Button variant='ghost' color='brand.solid' onClick={handleBack}>
            <Icon as={ArrowLeftIcon} w={4} h={4} />
            Back
          </Button>
        </Flex>
      </Flex>

      {isLoading && (
        <Flex justify='center' py={8}>
          <Spinner color='fg.subtle' />
        </Flex>
      )}

      {!isLoading && items.length === 0 && (
        <Text fontSize='sm' color='fg.subtle'>
          No curriculums yet.
        </Text>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
        {items.map((curriculum) => (
          <Card.Root
            key={curriculum.id}
            overflow='hidden'
            transition='all 0.18s ease'
            shadow='sm'
            _hover={{ shadow: 'md', transform: 'translateY(-2px)', borderColor: 'border.emphasized' }}
          >
            <Box as='button' type='button' onClick={() => handleOpen(curriculum)} textAlign='left' width='100%' cursor='pointer'>
              <Card.Body>
                <Stack gap={4} align='stretch'>
                  <Flex align='center' justify='space-between' gap={2}>
                    <Flex
                      align='center'
                      justify='center'
                      w={12}
                      h={12}
                      borderRadius='lg'
                      bg='slot6.bg'
                      color='slot6.fg'
                      flexShrink={0}
                    >
                      <Icon as={BookOpenIcon} w={6} h={6} />
                    </Flex>
                    <Badge variant='info' size='sm'>
                      {curriculum.source === 'partner' ? 'Partner' : 'Workspace'}
                    </Badge>
                  </Flex>

                  <Box>
                    <Text fontSize='lg' fontWeight='700' color='fg.strong' lineHeight='short'>
                      {curriculum.name}
                    </Text>
                    {curriculum.description && (
                      <Text fontSize='sm' color='fg.muted' mt={1.5}>
                        {curriculum.description}
                      </Text>
                    )}
                  </Box>

                  {/* What the curriculum promises and assumes — the two things
                      that decide whether this is the right one to pick. */}
                  <CurOutcomesSummary curriculum={curriculum} />

                  <Text
                    fontFamily='heading'
                    fontWeight='700'
                    fontSize='label'
                    textTransform='uppercase'
                    letterSpacing='label'
                    color='fg.subtle'
                  >
                    {curriculum.plan_count} plans ·{' '}
                    {curriculum.source === 'partner' ? 'Athlete Era' : workspace?.name ?? 'Workspace'}
                  </Text>
                </Stack>
              </Card.Body>
            </Box>
          </Card.Root>
        ))}
      </SimpleGrid>
    </Stack>
  );
}